import type { JSX } from 'react';
import { useState } from 'react';
import type { WireSourceInstance, WireSourceKind } from '@logistics/contracts';
import { cn } from '@/lib/utils';
import { monoLabel, segment } from '../shell/look';
import { Modal } from '../shell/modal';
import { catalogEntries, guideSteps, whatItReads } from './catalog';
import type { CatalogEntry } from './catalog';
import { CatalogGrid } from './catalog-grid';
import { GuideList } from './guide-list';
import { SourceForm } from './source-form';
import { TelegramLogin } from './telegram-login';

/**
 * "Add source" and "Update credentials" (design L595–646): pick a type, then
 * fill in what its descriptor asks for. Editing starts on step two.
 */
export function SourceModal({
  descriptors,
  editing,
  onClose,
  onSaved,
}: {
  descriptors: readonly WireSourceKind[];
  editing?: WireSourceInstance | undefined;
  onClose: () => void;
  onSaved: (message: string) => void;
}): JSX.Element {
  const entries = catalogEntries(descriptors);
  const [picked, setPicked] = useState<CatalogEntry | undefined>(() =>
    editing === undefined ? undefined : entries.find((e) => e.kind === editing.kind),
  );

  const eyebrow =
    editing !== undefined
      ? 'UPDATE CREDENTIALS'
      : picked !== undefined
        ? 'STEP 2 OF 2 · CREDENTIALS'
        : 'STEP 1 OF 2 · CHOOSE A SOURCE';

  return (
    <Modal
      eyebrow={eyebrow}
      title={picked?.name ?? 'What should the agent read from?'}
      width={900}
      tall
      onClose={onClose}
    >
      {picked?.descriptor === undefined ? (
        <CatalogGrid entries={entries} onPick={setPicked} />
      ) : (
        <Setup
          entry={picked}
          descriptor={picked.descriptor}
          editing={editing}
          back={
            editing === undefined
              ? { label: '← Choose another', run: () => setPicked(undefined) }
              : { label: 'Cancel', run: onClose }
          }
          onSaved={() =>
            onSaved(
              editing === undefined
                ? `${picked.name} connected`
                : 'Credentials updated and verified',
            )
          }
        />
      )}
    </Modal>
  );
}

/** Step two: what the agent will read and how to get the credentials, beside the form. */
function Setup({
  entry,
  descriptor,
  editing,
  back,
  onSaved,
}: {
  entry: CatalogEntry;
  descriptor: WireSourceKind;
  editing: WireSourceInstance | undefined;
  back: { label: string; run: () => void };
  onSaved: () => void;
}): JSX.Element {
  const loginPossible = descriptor.kind === 'telegram' && editing === undefined;
  const [viaLogin, setViaLogin] = useState(loginPossible);
  const steps = guideSteps(descriptor);

  return (
    <div className="grid min-h-0 grid-cols-1 overflow-y-auto tab:grid-cols-2 tab:overflow-hidden">
      <div className="bg-bg border-line overflow-y-auto border-b px-7 py-6 tab:border-r tab:border-b-0">
        <p className="mt-0 mb-[18px] text-[13.5px] leading-[1.6]">{whatItReads(descriptor.kind)}</p>
        <div className={cn(monoLabel, 'mb-2.5 text-[11px]')}>HOW TO GET THE CREDENTIALS</div>
        <GuideList steps={steps} />
      </div>
      <div className="flex flex-col gap-3.5 overflow-y-auto px-7 py-6">
        {loginPossible && <LoginChoice viaLogin={viaLogin} onChange={setViaLogin} />}
        {viaLogin ? (
          <TelegramLogin onDone={onSaved} back={back} />
        ) : (
          <SourceForm
            descriptor={descriptor}
            namePlaceholder={entry.namePlaceholder}
            existing={editing}
            onSaved={onSaved}
            back={back}
          />
        )}
      </div>
    </div>
  );
}

/** Telegram only: log in with the phone here, or paste a session string made elsewhere. */
function LoginChoice({
  viaLogin,
  onChange,
}: {
  viaLogin: boolean;
  onChange: (viaLogin: boolean) => void;
}): JSX.Element {
  const options = [
    { on: true, label: 'Log in with phone' },
    { on: false, label: 'Paste session string' },
  ];
  return (
    <div className={cn(segment.group, 'bg-surf2')} role="tablist" aria-label="How to connect">
      {options.map((option) => (
        <button
          key={option.label}
          type="button"
          role="tab"
          aria-selected={viaLogin === option.on}
          onClick={() => onChange(option.on)}
          className={cn(viaLogin === option.on ? segment.on : segment.off, 'flex-1')}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
